import React from 'react';
import type { ToolResultBlock, ToolUseBlock } from '../../../store/chatStore';
import clsx from 'clsx';
import {
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { safeJsonParse } from '../../../../../utils/jsonRepairUtils';
import { Image } from '../../../../ui/Image';
import {
  PleriActionCell,
  type MoreOption,
  type RemediationConfig,
} from './Table/components/PleriActionCell';
import type { GenericItem } from './Table/types';
import {
  isRecord,
  getString,
  getFirstString,
  getTags,
  getFirstTagSource,
  splitByAssetName,
} from './shared/fieldUtils';
import { getResourceImageFallback } from './shared/imageFallback';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

type ChipTone = 'neutral' | 'info' | 'warning' | 'good' | 'bad';

interface Props {
  result: ToolResultBlock;
  toolUse: ToolUseBlock;
}

interface ChipProps {
  label: string;
  tone?: ChipTone;
  size?: 'default' | 'compact';
}

// Names the tag lookup for the field utilities; not shown to anyone.
const FINDING_TAG_SOURCE = 'finding resourceTags';

const toneClasses: Record<ChipTone, string> = {
  neutral: 'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100',
  info: 'bg-sky-100 dark:bg-sky-900 text-sky-900 dark:text-sky-200',
  warning: 'bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-200',
  good: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-900 dark:text-emerald-200',
  bad: 'bg-rose-100 dark:bg-rose-900 text-rose-900 dark:text-rose-200',
};

const findingMoreOptions = (t: TFunction<'pleri/chat'>): MoreOption[] => [
  {
    label: t('suggestFix'),
    message: 'Suggest fix for this finding.',
  },
  {
    label: t('createTicket'),
    message: 'Can you help me create a ticket for this?',
  },
  {
    label: t('exempt'),
    message: 'Exempt this finding.',
  },
];

const findingInvestigateConfig = (
  t: TFunction<'pleri/chat'>,
): RemediationConfig => ({
  tooltip: t('investigate'),
  message: 'Investigate this finding',
  icon: MagnifyingGlassIcon,
  hoverClasses:
    'hover:bg-orange-100 hover:text-orange-700 hover:ring-orange-300',
});

const Chip: React.FC<ChipProps> = ({
  label,
  tone = 'neutral',
  size = 'default',
}) => {
  return (
    <span
      className={clsx(
        'inline-flex max-w-full items-center rounded-md font-normal',
        size === 'compact' ? 'px-2 py-0.5 text-xs' : 'px-2 py-1 text-xs',
        toneClasses[tone],
      )}
    >
      <span className="truncate">{label}</span>
    </span>
  );
};

function parseFindings(content: unknown): Record<string, unknown>[] {
  const parsed =
    typeof content === 'string'
      ? safeJsonParse<unknown>(content, 'listFindings result')
      : content;

  if (Array.isArray(parsed)) {
    return parsed.filter(isRecord);
  }

  if (
    isRecord(parsed) &&
    typeof parsed.content === 'string' &&
    ['{', '['].includes(parsed.content.trim().charAt(0))
  ) {
    return parseFindings(parsed.content);
  }

  if (!isRecord(parsed) || !Array.isArray(parsed.data)) {
    return [];
  }

  return parsed.data.filter(isRecord);
}

function toTitleCase(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getMetaRecord(
  finding: Record<string, unknown>,
): Record<string, unknown> {
  if (isRecord(finding.meta)) {
    return finding.meta;
  }
  return {};
}

function getSeverityChip(value: unknown): {
  label: string;
  classes: string;
} | null {
  const severity = getString(value)?.trim();
  if (!severity) {
    return null;
  }

  const normalized = severity.toLowerCase();
  if (normalized === 'critical' || normalized === 'high') {
    if (normalized === 'critical') {
      return {
        label: toTitleCase(severity),
        classes: 'bg-severity-critical-background text-severity-critical-base',
      };
    }
    return {
      label: toTitleCase(severity),
      classes: 'bg-severity-high-background text-severity-high-base',
    };
  }
  if (normalized === 'medium') {
    return {
      label: toTitleCase(severity),
      classes: 'bg-severity-medium-background text-severity-medium-base',
    };
  }
  if (normalized === 'low') {
    return {
      label: toTitleCase(severity),
      classes: 'bg-severity-low-background text-severity-low-base',
    };
  }
  if (normalized === 'info' || normalized === 'informational') {
    return {
      label: toTitleCase(severity),
      classes:
        'bg-severity-informational-background text-severity-informational-base',
    };
  }

  return {
    label: toTitleCase(severity),
    classes: 'bg-severity-unknown-background text-severity-unknown-base',
  };
}

function normalizeSummaryText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function getAssetName(finding: Record<string, unknown>): string | undefined {
  const meta = getMetaRecord(finding);
  const findingAsset = isRecord(finding.asset) ? finding.asset : {};
  return (
    getFirstString(
      finding.assetName,
      finding.asset_name,
      meta.assetName,
      meta.asset_name,
      findingAsset.name,
      finding.resourceName,
      finding.resource_name,
      meta.resourceName,
      meta.resource_name,
      findingAsset.resourceName,
      finding.name,
      finding.resourceId,
      finding.resource_id,
      meta.resourceId,
      meta.resource_id,
      findingAsset.resourceId,
    ) ?? undefined
  );
}

function withAssetName(summary: string, assetName?: string): string {
  if (!assetName) {
    return summary;
  }

  if (summary.toLowerCase().includes(assetName.toLowerCase())) {
    return summary;
  }

  return `${assetName}: ${summary}`;
}

export const FindingsTableRenderer: React.FC<Props> = ({ result }) => {
  const { t } = useTranslation('pleri/chat');
  const [visibleTagCountByFinding, setVisibleTagCountByFinding] =
    React.useState<Record<string, number>>({});

  if (result.isError) {
    return (
      <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950">
        {typeof result.content === 'string'
          ? result.content
          : JSON.stringify(result.content, null, 2)}
      </div>
    );
  }

  const findings = parseFindings(result.content);

  if (findings.length === 0) {
    return (
      <div className="py-2 text-sm text-slate-600 dark:text-slate-400">
        {t('noFindingsFoundForThisQuery')}
      </div>
    );
  }

  return (
    <div className="space-y-4 font-sans whitespace-normal">
      {findings.map((finding, index) => {
        const findingId = getString(finding.id) ?? `finding-${index}`;
        const assetName = getAssetName(finding);
        const summarySource =
          getString(finding.summary) ??
          getString(finding.rewrittenSummary) ??
          getString(finding.displaySummary) ??
          getString(finding.message) ??
          getString(finding.title) ??
          t('untitledFinding');
        const summary = normalizeSummaryText(
          withAssetName(summarySource, assetName),
        );
        const summaryParts = splitByAssetName(summary, assetName);
        const summaryContent = summaryParts ? (
          <>
            {summaryParts.before}
            <strong className="font-semibold">{summaryParts.match}</strong>
            {summaryParts.after}
          </>
        ) : (
          summary
        );
        const findingMeta = getMetaRecord(finding);
        const findingAsset = isRecord(finding.asset) ? finding.asset : {};
        const resourceType =
          getFirstString(
            finding.resourceType,
            finding.resource_type,
            findingAsset.resourceType,
            findingAsset.resource_type,
            findingMeta.resourceType,
            findingMeta.resource_type,
          ) ?? 'resource';
        const provider = getFirstString(
          finding.provider,
          finding.provider_name,
          findingAsset.provider,
          findingAsset.provider_name,
          findingMeta.provider,
          findingMeta.provider_name,
        );
        const severity = getSeverityChip(
          finding.severityLevel ?? finding.severity_level,
        );
        const tags = getTags(
          getFirstTagSource(
            FINDING_TAG_SOURCE,
            finding.resourceTags,
            finding.resource_tags,
            findingMeta.resourceTags,
            findingMeta.resource_tags,
            findingAsset.resourceTags,
            findingAsset.resource_tags,
            finding.tags,
            findingMeta.tags,
            findingAsset.tags,
          ),
          FINDING_TAG_SOURCE,
        );
        const visibleTagCount = visibleTagCountByFinding[findingId] ?? 2;
        const visibleTags = tags.slice(0, visibleTagCount);
        const hasMoreTags = visibleTagCount < tags.length;
        const nextTagCount = Math.min(visibleTagCount + 4, tags.length);
        const rawFindingId = getString(finding.id);
        const findingDetailUrl = rawFindingId
          ? `/findings/${btoa(rawFindingId)}`
          : undefined;

        return (
          <article
            key={findingId}
            className="group/asset-card relative min-h-[100px] overflow-visible rounded-xl border border-slate-200 dark:border-slate-700 bg-white p-4 pr-32 shadow-sm transition-shadow hover:shadow-md dark:bg-gray-900"
          >
            <PleriActionCell
              row={{
                original: {
                  ...(finding as GenericItem),
                  findingId,
                },
              }}
              showRemediationButton
              remediationConfig={findingInvestigateConfig(t)}
              moreOptions={findingMoreOptions(t)}
              position="right"
              hoverScope="asset-card"
              alwaysVisible
              size="compact"
              direction="vertical"
            />
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 flex-shrink-0 overflow-hidden rounded-md bg-slate-100 dark:bg-slate-700">
                <Image
                  type="resourceType"
                  name={resourceType}
                  fallback={getResourceImageFallback({
                    provider,
                    resourceType,
                  })}
                  className="h-full w-full object-contain p-0.5"
                />
              </div>
              {severity && (
                <span
                  className={clsx(
                    'inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-xs font-semibold',
                    severity.classes,
                  )}
                  title={t('severity')}
                >
                  {severity.label}
                </span>
              )}
            </div>
            <div
              className="mt-2 min-w-0 text-sm break-words whitespace-normal text-slate-800 dark:text-slate-200"
              title={t('findingSummary', { summary })}
            >
              {findingDetailUrl ? (
                <a
                  href={findingDetailUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-slate-800 hover:text-slate-900 dark:text-slate-200 dark:hover:text-slate-100"
                >
                  {summaryContent}
                  <span className="ml-1 inline-flex h-3.5 w-3.5 translate-y-[1px] items-center justify-center rounded bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300">
                    <ArrowTopRightOnSquareIcon className="h-2 w-2" />
                  </span>
                </a>
              ) : (
                summaryContent
              )}
            </div>
            {tags.length > 0 && (
              <div className="mt-3">
                <div className="text-xs font-normal tracking-[0.12em] text-slate-500 dark:text-slate-400 uppercase">
                  {t('tags')}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {visibleTags.map((tag, tagIndex) => (
                    <Chip
                      key={`${findingId}-tag-${tag.key}-${tag.value}-${tagIndex}`}
                      label={`${tag.key}: ${tag.value}`}
                    />
                  ))}
                  {hasMoreTags && (
                    <button
                      type="button"
                      onClick={() =>
                        setVisibleTagCountByFinding((prev) => ({
                          ...prev,
                          [findingId]: nextTagCount,
                        }))
                      }
                      className="inline-flex items-center rounded-md border border-slate-200 dark:border-slate-600 bg-white px-2 py-1 text-xs font-normal text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-500 hover:bg-slate-50 dark:bg-gray-900 dark:hover:bg-gray-800"
                      title={t('showMoreTags', {
                        count: Math.min(4, tags.length - visibleTagCount),
                      })}
                    >
                      {t('showMoreLabel')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
};
